import React, { useState } from "react";
import { DownloadIcon, FileTextIcon } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGetInvoiceQuery, useGetInvoicesQuery, useLazyGetInvoicePdfLinkQuery } from "@/store/rest_api";
import InvoiceDetailsSheet from "@/components/pages/billing/InvoiceDetailsSheet";
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_PILLS,
  formatInvoiceDate,
  formatInvoiceMoney,
  formatInvoicePeriod,
  invoiceDocumentName,
} from "@/util/invoice";
import { apiErrorMessage } from "@/util/apiError";

export function Invoices() {
  const [page, setPage] = useState(0);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const { data, isLoading } = useGetInvoicesQuery({ page, size: 25 });
  const { data: selectedInvoice, isFetching: isFetchingInvoice } = useGetInvoiceQuery(selectedInvoiceId ?? "", {
    skip: !selectedInvoiceId,
  });
  const [fetchPdfLink, { isFetching: isDownloading }] = useLazyGetInvoicePdfLinkQuery();

  const invoices = data?.items ?? [];

  const handleDownload = async (invoiceId: string) => {
    try {
      const link = await fetchPdfLink(invoiceId).unwrap();
      window.location.assign(link.url);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not download the invoice"));
    }
  };

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedInvoiceId(null);
    }
  };

  return (
    <div className="container p-4 text-foreground">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            Invoices
          </CardTitle>
          <CardDescription>
            An invoice for every billing cycle and a receipt for every top-up or credit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-12 text-center">
              <FileTextIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">No invoices yet. Your first one arrives at the end of your billing cycle.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="cursor-pointer" onClick={() => setSelectedInvoiceId(invoice.id)}>
                      <TableCell className="font-medium tabular-nums">{invoiceDocumentName(invoice)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {invoice.relatedNumber
                          ? `For ${invoice.relatedNumber}`
                          : formatInvoicePeriod(invoice.usagePeriodStart, invoice.usagePeriodEnd) ||
                            formatInvoicePeriod(invoice.planPeriodStart, invoice.planPeriodEnd)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatInvoiceDate(invoice.issuedAt)}</TableCell>
                      <TableCell>
                        <span className={INVOICE_STATUS_PILLS[invoice.status]}>{INVOICE_STATUS_LABELS[invoice.status]}</span>
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">{formatInvoiceMoney(invoice.total)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 rounded-full"
                          title="Download PDF"
                          aria-label="Download PDF"
                          disabled={isDownloading}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDownload(invoice.id);
                          }}
                        >
                          <DownloadIcon className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-end gap-3">
              <span className="text-sm text-muted-foreground tabular-nums">
                Page {data.page + 1} of {data.totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={data.page === 0} onClick={() => setPage((current) => current - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((current) => current + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <InvoiceDetailsSheet
        open={!!selectedInvoiceId}
        onOpenChange={handleSheetOpenChange}
        invoice={selectedInvoice}
        isLoading={isFetchingInvoice}
        isDownloading={isDownloading}
        onDownload={handleDownload}
      />
    </div>
  );
}
